// Detect title overflow on-demand to keep initial load light.
(() => {
  const selector = '.gamified-card__title-text';
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let resizeTimer = null;
  // Only titles we actually measured need resetting, so a full DOM sweep is never
  // required. On a collection page that sweep touched every card and forced a
  // document-wide style recalculation.
  const measured = new Set();

  const toNumber = (value) => {
    if (!value) return 0;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getFontString = (style) => {
    const fontStyle = style.fontStyle || 'normal';
    const fontVariant = style.fontVariant || 'normal';
    const fontWeight = style.fontWeight || 'normal';
    const fontSize = style.fontSize || '16px';
    const fontFamily = style.fontFamily || 'sans-serif';
    return `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`;
  };

  const measureTextWidth = (text, style) => {
    if (!ctx) return 0;
    ctx.font = getFontString(style);
    const metrics = ctx.measureText(text);
    const letterSpacing = style.letterSpacing && style.letterSpacing !== 'normal'
      ? toNumber(style.letterSpacing)
      : 0;
    return metrics.width + Math.max(0, text.length - 1) * letterSpacing;
  };

  const buildKey = (style, text, containerWidth) => {
    return [
      containerWidth,
      style.fontStyle,
      style.fontVariant,
      style.fontWeight,
      style.fontSize,
      style.fontFamily,
      style.letterSpacing,
      text.length
    ].join('|');
  };

  const updateTitle = (titleText) => {
    const container = titleText.closest('.gamified-card__title');
    if (!container) return;

    const containerWidth = container.clientWidth;
    if (!containerWidth) return;

    const style = window.getComputedStyle(titleText);
    const text = (titleText.textContent || '').trim();
    if (!text) return;

    const key = buildKey(style, text, containerWidth);
    if (titleText.dataset.overflowKey === key) return;
    titleText.dataset.overflowKey = key;
    measured.add(titleText);

    const textWidth = measureTextWidth(text, style);
    if (textWidth > containerWidth) {
      titleText.setAttribute('data-overflow', 'true');
      const scrollDistance = containerWidth - textWidth;
      titleText.style.setProperty('--scroll-distance', `${scrollDistance}px`);
    } else {
      titleText.removeAttribute('data-overflow');
      titleText.style.removeProperty('--scroll-distance');
    }
  };

  const clearAll = () => {
    measured.forEach((titleText) => {
      titleText.removeAttribute('data-overflow');
      titleText.style.removeProperty('--scroll-distance');
      delete titleText.dataset.overflowKey;
    });
    measured.clear();
  };

  const handlePointer = (event) => {
    const target = event.target.closest?.(selector);
    if (!target) return;
    updateTitle(target);
  };

  const init = () => {
    document.addEventListener('pointerenter', handlePointer, true);
    document.addEventListener('focusin', handlePointer, true);
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(clearAll, 150);
      },
      { passive: true }
    );

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(clearAll).catch(() => {});
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
